import React, { useState, useEffect, useMemo } from 'react';
import { Col, Modal, Row, Form, Button, Card, Badge } from 'react-bootstrap';
import axios from 'axios';
import { buildCableModel } from '../../helpers/cableGeometry.js';
import CableCrossSection3D from '../../components/standard/CableCrossSection3D.js';
import ConductorIsolation3D from '../../components/standard/ConductorIsolation3D.js';
import Phase3D, { beddingKeyOf } from '../../components/standard/Phase3D.js';
import MaterialPicker from '../../components/standard/MaterialPicker.js';
import StandardEditHistory from '../../components/standard/StandardEditHistory.js';
import { API } from '../../config/api';
import { suggestInternationalName } from '../../helpers/internationalName';
// One bedding rule for the whole app. See the note on `derivedForEdit` below for
// what having three of them cost.
import {
  deriveAtDiameter,
  isBedding,
  sumCoreDiscs
} from '../../helpers/standardCascade';

const API_URL = `${API.BACKEND}/api/v1/standard`;

// numeric fields at phase level
const PHASE_NUMERIC = new Set([
  'diameter', 'section', 'fillingSection', 'density',
  'minRadial', 'maxRadial', 'wide', 'thickness'
]);

// string fields at phase level
const PHASE_STRING = new Set(['material', 'type', 'dependsOn']);

// fields to skip in generic phase editor
const PHASE_SKIP = new Set(['name', 'conductors', 'rope', 'wire', 'isolation', 'lastPhase', 'dependsOn']);

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toNumArray = v => {
  if (!v) return [];
  if (!Array.isArray(v)) {
    if (typeof v === 'string') return v.split(',').map(s => Number(s.trim())).filter(Number.isFinite);
    const n = toNum(v);
    return n !== null ? [n] : [];
  }
  return v.map(x => Number(x)).filter(Number.isFinite);
};

const getColorHex = (c = '') => {
  const map = { BLUE: '#1976d2', BROWN: '#795548', 'GREEN-YELLOW': '#9acd32', BLACK: '#000', GREY: '#9e9e9e', RED: '#d32f2f', WHITE: '#f5f5f5' };
  return map[String(c).toUpperCase()] || '#ccc';
};

/**
 * Drop the form's own bookkeeping before the document is saved.
 *
 * `_specId` records which catalogue row the engineer picked. It is never part of
 * a standard, and it has to come off every nesting level the picker can reach:
 * the phase itself, each conductor's wire and insulation, and each rope build.
 */
const stripSpecIds = (phases = []) =>
  phases.map(phase => {
    const { _specId, ...rest } = phase || {};
    const out = { ...rest };

    if (Array.isArray(phase?.conductors)) {
      out.conductors = phase.conductors.map(c => {
        const next = { ...c };
        for (const group of ['wire', 'isolation']) {
          if (!next[group]) continue;
          const { _specId: _drop, ...block } = next[group];
          next[group] = block;
        }
        return next;
      });
    }
    // The flat FY/MYF shape keeps wire and isolation straight on the phase.
    for (const group of ['wire', 'isolation']) {
      if (!out[group]) continue;
      const { _specId: _drop, ...block } = out[group];
      out[group] = block;
    }
    if (Array.isArray(phase?.rope)) {
      out.rope = phase.rope.map(r => {
        const { _specId: _drop, ...rest2 } = r || {};
        return rest2;
      });
    }
    return out;
  });

const PI = Math.PI;
const disc = d => (PI * Number(d) * Number(d)) / 4;
const round4 = n => Number(Number(n).toFixed(4));
const fmt2 = n => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '—');

// circumscribed-circle packing ratio for n conductors twisted into a rope
const packRatio = n =>
  n <= 1 ? 1
    : n === 2 ? 2
      : n === 3 ? 1 + 1 / Math.sin(Math.PI / 3)
        : n === 4 ? 1 + Math.SQRT2
          : n === 5 ? 1 + 1 / Math.sin(Math.PI / 5)
            : 1 + 1 / Math.sin(Math.PI / n);

// Shows what a diameter edit CASCADES onto a downstream phase: the stored value
// (green) → the cascade-implied new value (red) for Ø / section / fillingSection,
// with an [Acceptă] button. Renders nothing when the phase is already consistent.
const CascadeReadout = ({ current, pred, onAccept }) => {
  if (!pred || !(pred.diameter > 0)) return null;
  const cur = Number(current?.diameter) || 0;
  const sCur = Number(current?.section) || 0;
  const fCur = Number(current?.fillingSection) || 0;
  const dDiff = cur > 0 && Math.abs(cur - pred.diameter) > 0.01;
  const sDiff = pred.section != null && Math.abs(sCur - pred.section) > 0.01;
  const fDiff = pred.fillingSection != null && Math.abs(fCur - pred.fillingSection) > 0.01;
  if (!dDiff && !sDiff && !fDiff) return null;
  return (
    <div className="mt-2 p-2 rounded border border-danger bg-danger bg-opacity-10 d-flex flex-wrap align-items-center gap-3" style={{ fontSize: 11 }}>
      <span className="fw-bold text-danger">Recalculare în lanț:</span>
      {dDiff && (<span>Ø <b className="text-success">{fmt2(cur)}</b> <span className="text-700">→</span> <b className="text-danger">{fmt2(pred.diameter)}</b> mm</span>)}
      {sDiff && (<span>Secțiune <b className="text-success">{fmt2(sCur)}</b> → <b className="text-danger">{fmt2(pred.section)}</b> mm²</span>)}
      {fDiff && (<span>Filling <b className="text-success">{fmt2(fCur)}</b> → <b className="text-danger">{fmt2(pred.fillingSection)}</b> mm²</span>)}
      <Button size="sm" variant="danger" className="py-0 px-2 ms-auto" style={{ fontSize: 10 }} onClick={onAccept}>Acceptă</Button>
    </div>
  );
};

const normalizeConductorIsolationPhase = phase => {
  if (!phase || phase.name !== 'conductor_isolation') return phase;
  if (Array.isArray(phase.conductors)) return phase;
  if (phase.wire && phase.isolation) {
    const { wire, isolation, ...rest } = phase;
    return { ...rest, conductors: [{ wire: { ...wire }, isolation: { ...isolation } }] };
  }
  return { ...phase, conductors: [] };
};

const StandardEditModal = ({ show, handleClose, standard, setData }) => {
  const [formData, setFormData] = useState({
    name: '', numberOfConductors: '', section: '',
    hasMixedConductorSections: false, greenYellowSection: '',
    internationalName: '',
    productStandard: '', performanceStandard: '',
    notifiedBody: '', dop: '', color: '',
    tension: '', fireReaction: '', phases: []
  });

  // snapshot of the phases as loaded — the baseline the cascade is anchored to
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    if (standard) {
      const normalizedPhases = (standard.phases || []).map(normalizeConductorIsolationPhase);
      setBaseline(JSON.parse(JSON.stringify(normalizedPhases)));
      setFormData({
        _id: standard._id,
        name: standard.name || '',
        numberOfConductors: standard.numberOfConductors || '',
        section: standard.section || '',
        hasMixedConductorSections: Boolean(
          standard.hasMixedConductorSections === true ||
          standard.hasMixedConductorSections === 'true'
        ),
        greenYellowSection: standard.greenYellowSection ?? '',
        internationalName: standard.internationalName || '',
        productStandard: standard.productStandard || '',
        performanceStandard: standard.performanceStandard || '',
        notifiedBody: standard.notifiedBody || '',
        dop: standard.dop || '',
        tension: standard.tension || '',
        color: standard.color || '',
        fireReaction: standard.fireReaction || '',
        phases: normalizedPhases
      });
    }
  }, [standard]);

  const handleChange = e => {
    const { name, value } = e.target;
    const numericFields = ['numberOfConductors', 'section', 'greenYellowSection'];
    if (numericFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // update a field inside a conductor's wire or isolation
  const updateConductorField = (phaseIdx, condIdx, group, field, value) => {
    setFormData(prev => {
      const phases = [...prev.phases];
      const phase = { ...phases[phaseIdx] };
      const conductors = [...phase.conductors];
      const cond = { ...conductors[condIdx] };
      const obj = { ...cond[group] };

      // determine type
      const isArray = ['numberOfWires', 'wireDiameter'].includes(field);
      const isString = ['material', 'type', 'color'].includes(field);

      if (isArray) {
        obj[field] = toNumArray(value);
      } else if (isString) {
        obj[field] = String(value).trim().toUpperCase();
      } else {
        obj[field] = value === '' ? '' : Number(value);
      }

      // editing a conductor's insulation Ø → recompute its section + fillingSection
      // (insulation ring over the solid conductor = disc(iso) − wire copper section)
      if (group === 'isolation' && field === 'diameter' && Number(value) > 0) {
        obj.section = round4(disc(Number(value)));
        const wireSection = Number(cond.wire?.section) || 0;
        if (wireSection > 0) obj.fillingSection = round4(Math.max(0, disc(Number(value)) - wireSection));
      }

      cond[group] = obj;
      conductors[condIdx] = cond;
      phase.conductors = conductors;
      phases[phaseIdx] = phase;
      return { ...prev, phases };
    });
  };

  // update a field on a phase
  const updatePhaseField = (phaseIdx, field, value) => {
    setFormData(prev => {
      const phases = [...prev.phases];
      const phase = { ...phases[phaseIdx] };

      if (PHASE_NUMERIC.has(field)) {
        phase[field] = value === '' ? '' : Number(value);
      } else if (PHASE_STRING.has(field)) {
        phase[field] = String(value).trim().toUpperCase();
      } else {
        phase[field] = value;
      }

      phases[phaseIdx] = phase;

      // editing a chain phase's diameter → recompute its own section + fillingSection
      if (field === 'diameter' && Number(phase.diameter) > 0 &&
        ['rope', 'interior_sheat', 'exterior_sheat', 'armouring'].includes(phase.name)) {
        phases[phaseIdx] = { ...phase, ...derivedForEdit(phases, phaseIdx, Number(phase.diameter)) };
      }

      return { ...prev, phases };
    });
  };

  /**
   * Take material, type, density (and colour) from the catalogue in one go.
   *
   * Why this is here at all: every material field in this modal was free text,
   * and on 2026-10-14 two new RVK standards arrived with their outer sheath
   * typed as `YM2` and `YM2 BLACK` while the other thirteen said `YM2 NEGRU` —
   * three names for one compound. That is not cosmetic: a BOM line is keyed as
   * `PVC|${phase.type}|${phase.name}`, so Standard Calculation requisitioned the
   * same shelf three times. The geometry stays typed by hand; the NAME should
   * never be.
   *
   * Picking nothing is allowed — a genuinely new compound has to be enterable
   * before anybody has catalogued it.
   */
  const applySpecToPhase = (phaseIdx, spec) => {
    if (!spec) return;
    setFormData(prev => {
      const phases = [...prev.phases];
      phases[phaseIdx] = {
        ...phases[phaseIdx],
        material: String(spec.material ?? phases[phaseIdx].material ?? '').toUpperCase(),
        type: String(spec.type ?? phases[phaseIdx].type ?? '').toUpperCase(),
        ...(spec.density != null ? { density: Number(spec.density) } : {}),
        _specId: spec._id
      };
      return { ...prev, phases };
    });
  };

  /** The same, for a conductor's wire or insulation block. */
  const applySpecToConductor = (phaseIdx, condIdx, group, spec) => {
    if (!spec) return;
    setFormData(prev => {
      const phases = [...prev.phases];
      const phase = { ...phases[phaseIdx] };
      const conductors = [...(phase.conductors || [])];
      const cond = { ...conductors[condIdx] };
      const block = { ...(cond[group] || {}) };

      block.material = String(spec.material ?? block.material ?? '').toUpperCase();
      block.type = String(spec.type ?? block.type ?? '').toUpperCase();
      if (spec.density != null) block.density = Number(spec.density);
      // A catalogued colour is taken only when it has one; an insulation's
      // colour is a property of THIS core, and the catalogue holds one row per
      // colour, so overwriting it with a blank would lose the core's identity.
      if (group === 'isolation' && spec.color) block.color = String(spec.color).toUpperCase();
      block._specId = spec._id;

      cond[group] = block;
      conductors[condIdx] = cond;
      phase.conductors = conductors;
      phases[phaseIdx] = phase;
      return { ...prev, phases };
    });
  };

  /** …and for one rope build inside pre_stranding. */
  const applySpecToRope = (phaseIdx, ropeIdx, spec) => {
    if (!spec) return;
    setFormData(prev => {
      const phases = [...prev.phases];
      const phase = { ...phases[phaseIdx] };
      const ropes = [...(phase.rope || [])];
      ropes[ropeIdx] = {
        ...ropes[ropeIdx],
        material: String(spec.material ?? ropes[ropeIdx].material ?? '').toUpperCase(),
        type: String(spec.type ?? ropes[ropeIdx].type ?? '').toUpperCase(),
        ...(spec.density != null ? { density: Number(spec.density) } : {}),
        _specId: spec._id
      };
      phase.rope = ropes;
      phases[phaseIdx] = phase;
      return { ...prev, phases };
    });
  };

  // update a field inside a pre_stranding rope entry
  const updateRopeField = (phaseIdx, ropeIdx, field, value) => {
    setFormData(prev => {
      const phases = [...prev.phases];
      const phase = { ...phases[phaseIdx] };
      const ropes = [...(phase.rope || [])];
      const rope = { ...ropes[ropeIdx] };

      const isArray = ['numberOfWires', 'wireDiameter'].includes(field);
      const isString = ['material', 'type'].includes(field);

      if (isArray) {
        rope[field] = toNumArray(value);
      } else if (isString) {
        rope[field] = String(value).trim().toUpperCase();
      } else {
        rope[field] = value === '' ? '' : Number(value);
      }

      ropes[ropeIdx] = rope;
      phase.rope = ropes;
      phases[phaseIdx] = phase;
      return { ...prev, phases };
    });
  };

  /**
   * Section + filling for a directly-edited chain phase diameter.
   *
   * Both this and the cascade below used to carry their own copy of the bedding
   * rule, and both got the rope-less cables wrong: a sheath sitting straight on
   * the cores (the 8 MYYUP flat cables — `conductor_isolation` then
   * `exterior_sheat`, nothing between) failed their "is the previous layer the
   * rope" test, so editing its diameter wrote the WHOLE disc as filling
   * — 38.48 mm² where the stored, migrated and analytics-agreed value is 26.17,
   * a 47 % over-count of PVC on the family the interstitial migration was for.
   *
   * There is one rule now, in helpers/standardCascade.js, and Standard
   * Analytics, create-standard, the new wizard and this modal all ask it.
   */
  const derivedForEdit = (phases, idx, d) =>
    deriveAtDiameter({
      phases,
      index: idx,
      diameter: d,
      numberOfConductors: formData.numberOfConductors
    });

  // ── Delta-anchored cascade (like Standard Analytics) ──
  // Each downstream chain phase inherits the outward shift caused by an upstream
  // diameter change, preserving its own wall. A phase whose stored Ø matches neither
  // the baseline nor the inherited value is treated as a deliberate manual anchor and
  // is respected (so accepting a value keeps cascading without needing a re-baseline).
  const buildEditCascade = () => {
    const phases = formData.phases;
    const pred = {};
    if (!baseline) return { pred };

    const ci = phases.find(p => p.name === 'conductor_isolation');
    const baseCi = baseline.find(p => p.name === 'conductor_isolation');
    const nc = Number(formData.numberOfConductors) || ci?.conductors?.length || 1;
    const isoD = c => Number(c?.isolation?.diameter) || 0;
    const curIso = (ci?.conductors || []).map(isoD).filter(d => d > 0);
    const baseIso = (baseCi?.conductors || []).map(isoD).filter(d => d > 0);
    const curIsoMax = curIso.length ? Math.max(...curIso) : 0;
    const baseIsoMax = baseIso.length ? Math.max(...baseIso) : 0;
    const coreDiscs = sumCoreDiscs(phases, nc);
    const deltaCore = curIsoMax - baseIsoMax;
    const packR = packRatio(nc);

    let expPrev = curIsoMax;
    let basePrev = baseIsoMax;

    for (let i = 0; i < phases.length; i++) {
      const name = phases[i].name;
      if (name !== 'rope' && name !== 'interior_sheat' && name !== 'exterior_sheat' && name !== 'armouring') continue;
      const baseOuter = Number(baseline[i]?.diameter) || 0;
      const curOuter = Number(phases[i].diameter) || 0;
      const inherited = name === 'rope'
        ? round4(baseOuter + packR * deltaCore)
        : round4(baseOuter + (expPrev - basePrev));
      const manualAnchor = curOuter > 0 && Math.abs(curOuter - baseOuter) > 0.001 && Math.abs(curOuter - inherited) > 0.01;
      const expected = manualAnchor ? curOuter : inherited;

      const section = round4(disc(expected));
      let fillingSection;
      if (name === 'interior_sheat' || name === 'exterior_sheat') {
        // The same question the shared helper answers, asked of the phase list
        // rather than of a running "previous layer" that only tracked rope,
        // sheaths and armour — so a sheath straight on the cores, with no rope
        // in the cable at all, was never recognised as the bedding.
        const bedding = isBedding(phases, i);
        fillingSection = round4(
          Math.max(0, section - (bedding ? coreDiscs : disc(expPrev)))
        );
      }
      pred[i] = { diameter: expected, section, ...(fillingSection != null ? { fillingSection } : {}) };

      basePrev = baseOuter || basePrev;
      expPrev = expected || expPrev;
    }
    return { pred };
  };

  const applyCascade = idx => {
    const p = buildEditCascade().pred[idx];
    if (!p || !(p.diameter > 0)) return;
    setFormData(prev => {
      const phases = [...prev.phases];
      phases[idx] = { ...phases[idx], diameter: p.diameter, section: p.section, ...(p.fillingSection != null ? { fillingSection: p.fillingSection } : {}) };
      return { ...prev, phases };
    });
  };

  const applyAllCascade = () => {
    const { pred } = buildEditCascade();
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((ph, i) => {
        const p = pred[i];
        if (!p || !(p.diameter > 0)) return ph;
        return { ...ph, diameter: p.diameter, section: p.section, ...(p.fillingSection != null ? { fillingSection: p.fillingSection } : {}) };
      })
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        hasMixedConductorSections: Boolean(formData.hasMixedConductorSections),
        numberOfConductors: Number(formData.numberOfConductors),
        section: Number(formData.section),
        greenYellowSection: formData.hasMixedConductorSections
          ? Number(formData.greenYellowSection) : null,
      };

      // `edits` is the document's own history — the server appends to it, and
      // sending it back would overwrite the entry being added.
      delete submitData.edits;

      // `_specId` is which catalogue row was picked, and it belongs to this form
      // only. Left in, it would be saved onto every phase of every standard
      // edited from here — a field no reader knows about, and one the audit diff
      // would then report as a change on every save.
      submitData.phases = stripSpecIds(submitData.phases);

      // No actor header: this page is behind the login, the token goes with
      // every axios call, and :5000 reads the name off the authenticated user
      // document — which is the one answer the caller cannot choose.
      const res = await axios.patch(`${API_URL}/${standard._id}`, submitData);

      setData(prevData =>
        prevData.map(item => (item._id === standard._id ? { ...item, ...submitData } : item))
      );

      const changed = res?.data?.changes?.length || 0;
      alert(
        changed
          ? `Standard updated — ${changed} change${changed === 1 ? '' : 's'} recorded in the activity timeline.`
          : 'Standard saved. Nothing had changed, so nothing was recorded.'
      );
      handleClose();
    } catch (error) {
      console.error('Error updating standard:', error);
      alert('Failed to update standard.');
    }
  };

  // ── render helpers ──

  const renderWireFields = (wire, phaseIdx, condIdx) => {
    if (!wire) return null;
    const picker = (
      <Col xs={12} key="_pick">
        <MaterialPicker
          usedIn="conductor_isolation.wire"
          value={wire._specId}
          onPick={spec => applySpecToConductor(phaseIdx, condIdx, 'wire', spec)}
          label="Quick select"
        />
      </Col>
    );
    const fields = [
      { key: 'material', label: 'Material', type: 'string' },
      { key: 'type', label: 'Type', type: 'string' },
      { key: 'density', label: 'Density', type: 'number' },
      { key: 'diameter', label: 'Diameter', type: 'number' },
      { key: 'section', label: 'Section', type: 'number' },
      { key: 'numberOfWires', label: 'Nr. Wires', type: 'array' },
      { key: 'wireDiameter', label: 'Wire Ø', type: 'array' },
    ];

    return [
      picker,
      ...fields.map(f => {
        const val = wire[f.key];
        if (val === undefined || val === null) return null;
        return (
          <Col xs={6} key={f.key}>
            <Form.Label className="mb-0" style={{ fontSize: 11 }}>{f.label}</Form.Label>
            <Form.Control
              size="sm"
              type={f.type === 'number' ? 'number' : 'text'}
              step="any"
              value={f.type === 'array' ? (Array.isArray(val) ? val.join(', ') : val) : val}
              onChange={e => updateConductorField(phaseIdx, condIdx, 'wire', f.key, e.target.value)}
            />
          </Col>
        );
      })
    ];
  };

  const renderIsolationFields = (iso, phaseIdx, condIdx) => {
    if (!iso) return null;
    const fields = [
      { key: 'material', label: 'Material' },
      { key: 'type', label: 'Type' },
      { key: 'density', label: 'Density' },
      { key: 'diameter', label: 'Diameter' },
      { key: 'section', label: 'Section' },
      { key: 'fillingSection', label: 'Filling Section' },
      { key: 'minRadial', label: 'Min Radial' },
      { key: 'maxRadial', label: 'Max Radial' },
      { key: 'color', label: 'Color' },
    ];

    const picker = (
      <Col xs={12} key="_pick">
        <MaterialPicker
          usedIn="conductor_isolation.isolation"
          value={iso._specId}
          onPick={spec => applySpecToConductor(phaseIdx, condIdx, 'isolation', spec)}
          label="Quick select"
        />
      </Col>
    );

    return [picker, ...fields.map(f => {
      const val = iso[f.key];
      if (val === undefined || val === null) return null;
      const isStr = ['material', 'type', 'color'].includes(f.key);
      return (
        <Col xs={6} key={f.key}>
          <Form.Label className="mb-0" style={{ fontSize: 11 }}>
            {f.label}
            {f.key === 'color' && val && (
              <span
                style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: getColorHex(val), marginLeft: 4, border: '1px solid #555' }}
              />
            )}
          </Form.Label>
          <Form.Control
            size="sm"
            type={isStr ? 'text' : 'number'}
            step="any"
            value={val}
            onChange={e => updateConductorField(phaseIdx, condIdx, 'isolation', f.key, e.target.value)}
          />
        </Col>
      );
    })];
  };

  const model = useMemo(() => buildCableModel(formData, { length: 1000, layLength: 0 }), [formData]);
  const editBeddingKey = useMemo(() => beddingKeyOf(model), [model]);

  const cascade = buildEditCascade();
  const staleCount = formData.phases.filter((ph, i) => {
    const p = cascade.pred[i];
    if (!p || !(p.diameter > 0)) return false;
    const dDiff = Number(ph.diameter) > 0 && Math.abs(Number(ph.diameter) - p.diameter) > 0.01;
    const fDiff = p.fillingSection != null && Math.abs((Number(ph.fillingSection) || 0) - p.fillingSection) > 0.01;
    return dDiff || fDiff;
  }).length;

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Edit Standard: {formData.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Before the fields, not after them: the question "who changed this,
            and to what?" is asked while looking at a value that surprises you,
            which is before you touch anything. */}
        <StandardEditHistory edits={standard?.edits} />

        <Form onSubmit={handleSubmit}>
          {/* Basic info (left) + full-cable 3D (right) */}
          <Row className="g-3 mb-2">
            <Col lg={8}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" value={formData.name} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Conductors</Form.Label>
                <Form.Control type="number" name="numberOfConductors" value={formData.numberOfConductors} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Section</Form.Label>
                <Form.Control type="number" name="section" value={formData.section} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Mixed</Form.Label>
                <Form.Check
                  type="switch"
                  checked={formData.hasMixedConductorSections}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    hasMixedConductorSections: e.target.checked,
                    greenYellowSection: e.target.checked ? prev.greenYellowSection : ''
                  }))}
                />
              </Form.Group>
            </Col>
            {formData.hasMixedConductorSections && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>GY Section</Form.Label>
                  <Form.Control type="number" name="greenYellowSection" value={formData.greenYellowSection} onChange={handleChange} />
                </Form.Group>
              </Col>
            )}
          </Row>

          <Row className="g-3 mt-2">
            <Col md={6}>
              <Form.Group>
                {/* Printed instead of the name on an export order, and only
                    then. Everything else stays — same cable, same certificate. */}
                <Form.Label>International Name</Form.Label>
                <Form.Control
                  name="internationalName"
                  value={formData.internationalName}
                  onChange={handleChange}
                  placeholder={
                    suggestInternationalName(formData.name, formData.section) || 'none'
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Product Standard</Form.Label>
                <Form.Control name="productStandard" value={formData.productStandard} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Performance Standard</Form.Label>
                <Form.Control name="performanceStandard" value={formData.performanceStandard} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Notified Body</Form.Label>
                <Form.Control name="notifiedBody" value={formData.notifiedBody} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>DOP</Form.Label>
                <Form.Control name="dop" value={formData.dop} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tension</Form.Label>
                <Form.Control name="tension" value={formData.tension} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Fire Reaction</Form.Label>
                <Form.Control name="fireReaction" value={formData.fireReaction} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Color</Form.Label>
                <Form.Select name="color" value={formData.color} onChange={handleChange}>
                  <option value="">Select</option>
                  <option value="WHITE">WHITE</option>
                  <option value="BLACK">BLACK</option>
                  <option value="GREY">GREY</option>
                  <option value="GREY 7004">GREY 7004</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
            </Col>
            <Col lg={4}>
              {model && model.cores?.length > 0 && (
                <div style={{ position: 'sticky', top: 8 }}>
                  <h6 className="fw-bold mb-2">Full cable — 3D</h6>
                  <CableCrossSection3D model={model} height={460} />
                </div>
              )}
            </Col>
          </Row>

          <h5 className="mt-4 mb-3">Phases</h5>

          {staleCount > 0 && (
            <div className="mb-3 p-2 rounded border border-warning bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
              <span style={{ fontSize: 13 }}>
                <b>{staleCount}</b> {staleCount === 1 ? 'fază are' : 'faze au'} diametrul/filling incompatibil cu geometria curentă
                (s-a schimbat ceva upstream). Valorile din câmpuri nu au fost modificate automat.
              </span>
              <Button variant="warning" size="sm" onClick={applyAllCascade}>
                Recalculează tot cablul
              </Button>
            </div>
          )}

          {formData.phases.map((phase, phaseIdx) => {
            if (!phase || typeof phase !== 'object') return null;

            return (
              <Card key={phaseIdx} className="mb-3 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                  <span className="fw-bold text-primary">
                    {(phase.name || '').replaceAll('_', ' ').toUpperCase()}
                  </span>
                  <div className="d-flex gap-2 align-items-center">
                    {phase.lastPhase && <Badge bg="success">Last Phase</Badge>}
                    {phase.dependsOn && (
                      <span className="text-muted fs-10">
                        depends on: {phase.dependsOn}
                      </span>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="py-2">
                  <Row className="g-3">
                    <Col lg={phase.name === 'conductor_isolation' ? 12 : 7}>

                  {/* ── CONDUCTOR ISOLATION ── */}
                  {phase.name === 'conductor_isolation' && phase.conductors?.map((cond, condIdx) => (
                    <Card key={condIdx} className="mb-2 border-secondary-subtle">
                      <Card.Header className="py-1 d-flex align-items-center gap-2">
                        <span className="fw-semibold" style={{ fontSize: 13 }}>
                          Conductor {condIdx + 1}
                        </span>
                        {cond.isolation?.color && (
                          <>
                            <span
                              style={{ width: 12, height: 12, borderRadius: '50%', background: getColorHex(cond.isolation.color), display: 'inline-block', border: '1px solid #555' }}
                            />
                            <span className="text-muted" style={{ fontSize: 11 }}>{cond.isolation.color}</span>
                          </>
                        )}
                      </Card.Header>
                      <Card.Body className="py-2">
                        <Row className="g-2">
                          <Col lg={8}>
                            <Row className="g-2">
                              <Col md={6}>
                                <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>Wire</div>
                                <Row className="g-1">
                                  {renderWireFields(cond.wire, phaseIdx, condIdx)}
                                </Row>
                              </Col>
                              <Col md={6}>
                                <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>Isolation</div>
                                <Row className="g-1">
                                  {renderIsolationFields(cond.isolation, phaseIdx, condIdx)}
                                </Row>
                              </Col>
                            </Row>
                          </Col>
                          <Col lg={4}>
                            {model?.cores?.[condIdx] && (
                              <ConductorIsolation3D model={{ cores: [model.cores[condIdx]] }} height={240} />
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}

                  {/* ── PRE_STRANDING ── */}
                  {phase.name === 'pre_stranding' && Array.isArray(phase.rope) && phase.rope.map((rope, ropeIdx) => (
                    <Card key={ropeIdx} className="mb-2 border-info-subtle">
                      <Card.Header className="py-1">
                        <span className="fw-semibold" style={{ fontSize: 13 }}>
                          Rope #{ropeIdx + 1}
                        </span>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <MaterialPicker
                          usedIn="pre_stranding"
                          value={rope._specId}
                          onPick={spec => applySpecToRope(phaseIdx, ropeIdx, spec)}
                          label="Quick select"
                        />
                        <Row className="g-2">
                          {[
                            { key: 'material', label: 'Material', type: 'string' },
                            { key: 'type', label: 'Type', type: 'string' },
                            { key: 'density', label: 'Density', type: 'number' },
                            { key: 'diameter', label: 'Diameter', type: 'number' },
                            { key: 'numberOfDrums', label: 'Nr. Drums', type: 'number' },
                            { key: 'numberOfWires', label: 'Nr. Wires', type: 'array' },
                            { key: 'wireDiameter', label: 'Wire Ø', type: 'array' },
                          ].map(f => {
                            const val = rope[f.key];
                            if (val === undefined) return null;
                            return (
                              <Col md={3} key={f.key}>
                                <Form.Label className="mb-0" style={{ fontSize: 11 }}>{f.label}</Form.Label>
                                <Form.Control
                                  size="sm"
                                  type={f.type === 'number' ? 'number' : 'text'}
                                  step="any"
                                  value={f.type === 'array' ? (Array.isArray(val) ? val.join(', ') : val) : (val ?? '')}
                                  onChange={e => updateRopeField(phaseIdx, ropeIdx, f.key, e.target.value)}
                                />
                              </Col>
                            );
                          })}
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}

                  {/* ── GENERIC PHASE FIELDS (rope, sheat, armouring) ── */}
                  {phase.name !== 'conductor_isolation' && phase.name !== 'pre_stranding' && (
                    <>
                      {/* A sheath or an armour tape has a compound; the rope is
                          bare twisted copper and has none to pick. */}
                      {phase.material !== undefined && (
                        <MaterialPicker
                          usedIn={phase.name}
                          value={phase._specId}
                          onPick={spec => applySpecToPhase(phaseIdx, spec)}
                          label="Quick select from the catalogue"
                        />
                      )}
                      <Row className="g-2">
                        {Object.entries(phase)
                          .filter(([key]) => !PHASE_SKIP.has(key))
                          .map(([key, value]) => {
                            const isNum = PHASE_NUMERIC.has(key);
                            return (
                              <Col md={3} key={key}>
                                <Form.Label className="mb-0 text-capitalize" style={{ fontSize: 11 }}>
                                  {key.replaceAll('_', ' ')}
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type={isNum ? 'number' : 'text'}
                                  step="any"
                                  value={value ?? ''}
                                  onChange={e => updatePhaseField(phaseIdx, key, e.target.value)}
                                />
                              </Col>
                            );
                          })}
                      </Row>
                      <CascadeReadout
                        current={{ diameter: phase.diameter, section: phase.section, fillingSection: phase.fillingSection }}
                        pred={cascade.pred[phaseIdx]}
                        onAccept={() => applyCascade(phaseIdx)}
                      />
                    </>
                  )}
                    </Col>
                    {phase.name !== 'conductor_isolation' && (
                      <Col lg={5}>
                        <Phase3D phase={phase} model={model} beddingKey={editBeddingKey} productColor={formData.color} height={300} />
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            );
          })}

          <div className="mt-3 text-end">
            <Button variant="secondary" className="me-2" onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="primary">Update Standard</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default StandardEditModal;
